import {
  Burner,
  Cupboard,
  RangeModel,
  Oven,
  RangeTop,
} from '../../shared/Enums';
import { ampsLookup } from '../../shared/rangeLookups';
import { RangeState } from '../../shared/RangeState';

export class Rules {
  /**
   * The maximum electrical draw of a range in amps.
   * @readonly
   * @type {number}
   */
  static get MAX_RANGE_AMPS() {
    return 60;
  }

  /**
   * The configured state of the range.
   * @type {RangeState}
   */
  #state;

  /**
   * @param {RangeState} state
   */
  constructor(state) {
    this.#state = state;
  }

  /**
   * For almost all 2 oven ranges, if one oven is gas and the other oven is
   * electric, the gas oven has to be on the left. This rule does not apply to
   * any of the Chagny ranges.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftOven]
   * @param {string} [desired.rightOven]
   * @returns {boolean}
   */
  hasLeftElectricRightGasOvens(desired) {
    // The Chagny ranges will always have a left electric oven. The right oven may be gas.
    if (
      [
        RangeModel.chagny,
        RangeModel.chagny1400left,
        RangeModel.chagny1400right,
        RangeModel.chagny1800,
      ].includes(this.#state.model)
    ) {
      return false;
    }

    const leftOven = desired.leftOven || this.#state.leftOven;
    const rightOven = desired.rightOven || this.#state.rightOven;

    return this.#isElectric(leftOven) && rightOven === Oven.gas;
  }

  /**
   * The electrical draw of a range may not exceed 60 amps.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftCupboard]
   * @param {string} [desired.leftOven]
   * @param {string} [desired.rightOven]
   * @param {string} [desired.rightCupboard]
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @param {string} [desired.option4]
   * @returns {boolean}
   */
  isOverMaxAmps(desired) {
    const leftCupboard = desired.leftCupboard || this.#state.leftCupboard;
    const leftOven = desired.leftOven || this.#state.leftOven;
    const rightOven = desired.rightOven || this.#state.rightOven;
    const rightCupboard = desired.rightCupboard || this.#state.rightCupboard;
    const option1 = desired.option1 || this.#state.option1;
    const option2 = desired.option2 || this.#state.option2;
    const option3 = desired.option3 || this.#state.option3;
    const option4 = desired.option4 || this.#state.option4;

    const desiredAmps =
      ampsLookup[leftCupboard] +
      ampsLookup[this.#state.petiteOven] +
      ampsLookup[leftOven] +
      ampsLookup[rightOven] +
      ampsLookup[rightCupboard] +
      ampsLookup[option1] +
      ampsLookup[option2] +
      ampsLookup[option3] +
      ampsLookup[option4];

    return desiredAmps > Rules.MAX_RANGE_AMPS;
  }

  /**
   * A plancha cannot be placed over a gas oven. This only applies to the
   * various Sully ranges.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftOven]
   * @param {string} [desired.rightOven]
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @returns {boolean}
   */
  hasPlanchaOverGasOven(desired) {
    return this.#hasElectricOptionOverGasOven(desired, Burner.electricPlancha);
  }

  /**
   * Induction rings cannot be placed over a gas oven. This only applies to the
   * various Sully ranges.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftOven]
   * @param {string} [desired.rightOven]
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @returns {boolean}
   */
  hasInductionRingsOverGasOven(desired) {
    return this.#hasElectricOptionOverGasOven(
      desired,
      Burner.twoInductionRings
    );
  }

  /**
   * The Multi-Cooker can only go over a storage cupboard. It cannot be placed
   * over a warming cupboard.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftCupboard]
   * @param {string} [desired.rightCupboard]
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @param {string} [desired.option4]
   * @returns {boolean}
   */
  hasMultiCookerOverWarmingCupboard(desired) {
    const leftCupboard = desired.leftCupboard || this.#state.leftCupboard;
    const rightCupboard = desired.rightCupboard || this.#state.rightCupboard;
    const option1 = desired.option1 || this.#state.option1;
    const option2 = desired.option2 || this.#state.option2;
    const option3 = desired.option3 || this.#state.option3;
    const option4 = desired.option4 || this.#state.option4;

    if (
      (this.#state.model === RangeModel.chagny1400left ||
        this.#state.model === RangeModel.chagny1800 ||
        this.#state.model === RangeModel.chassagne ||
        this.#state.model === RangeModel.cluny1400left ||
        this.#state.model === RangeModel.cluny1800 ||
        this.#state.model === RangeModel.fontenay ||
        this.#state.model === RangeModel.sully1800left ||
        this.#state.model === RangeModel.sully2200) &&
      leftCupboard === Cupboard.warming &&
      option1 === Burner.multiCooker
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.chagny1400right ||
        this.#state.model === RangeModel.chambertin ||
        this.#state.model === RangeModel.cluny1400right) &&
      rightCupboard === Cupboard.warming &&
      option1 === Burner.multiCooker
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.chagny1800 ||
        this.#state.model === RangeModel.citeaux ||
        this.#state.model === RangeModel.cluny1800 ||
        this.#state.model === RangeModel.fontenay) &&
      rightCupboard === Cupboard.warming &&
      option2 === Burner.multiCooker
    ) {
      return true;
    }

    if (
      this.#state.model === RangeModel.sully1800right &&
      rightCupboard === Cupboard.warming &&
      option3 === Burner.multiCooker
    ) {
      return true;
    }

    if (
      this.#state.model === RangeModel.sully2200 &&
      rightCupboard === Cupboard.warming &&
      option4 === Burner.multiCooker
    ) {
      return true;
    }

    return false;
  }

  /**
   * The Multi-Cooker can only go over a storage cupboard. It cannot be placed
   * over a vertical convection oven.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.option1]
   * @returns {boolean}
   */
  hasMultiCookerOverVCT(desired) {
    if (
      (this.#state.model === RangeModel.citeaux ||
        this.#state.model === RangeModel.saulieu ||
        this.#state.model === RangeModel.savigny) &&
      desired.option1 === Burner.multiCooker
    ) {
      return true;
    }

    return false;
  }

  /**
   * The Flame Grill cannot be placed over a vertical convection oven.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.option1]
   * @returns {boolean}
   */
  hasFlameGrillOverVCT(desired) {
    if (
      (this.#state.model === RangeModel.citeaux ||
        this.#state.model === RangeModel.saulieu ||
        this.#state.model === RangeModel.savigny) &&
      desired.option1 === Burner.flameGrill
    ) {
      return true;
    }

    return false;
  }

  /**
   * The traditional plate cannot be placed to the right of a Sully 4 feux top.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @param {string} [desired.top]
   * @returns {boolean}
   */
  hasTraditionalPlateToRightOfSully4Feux(desired) {
    const option2 = desired.option2 || this.#state.option2;
    const option3 = desired.option3 || this.#state.option3;
    const top = desired.top || this.#state.top;

    if (
      (this.#state.model === RangeModel.sully ||
        this.#state.model === RangeModel.sully1800right) &&
      top === RangeTop.fourFeux &&
      option2 === Burner.traditionalSimmerPlate
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.sully1800left ||
        this.#state.model === RangeModel.sully2200) &&
      top === RangeTop.fourFeux &&
      option3 === Burner.traditionalSimmerPlate
    ) {
      return true;
    }

    return false;
  }

  /**
   * Induction rings cannot be placed in the left optional space of a Citeaux or
   * Fontenay Traditional range top. Induction rings are also not allowed on any
   * of the 1100mm wide ranges with a Traditional range top.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.option1]
   * @param {string} [desired.top]
   * @returns {boolean}
   */
  hasInductionRingsInProhibitedPositions(desired) {
    const option1 = desired.option1 || this.#state.option1;
    const top = desired.top || this.#state.top;

    if (
      (this.#state.model === RangeModel.fontenay ||
        this.#state.model === RangeModel.citeaux ||
        this.#state.model === RangeModel.chambertin ||
        this.#state.model === RangeModel.chassagne ||
        this.#state.model === RangeModel.savigny ||
        this.#state.model === RangeModel.saulieu) &&
      top === RangeTop.traditional &&
      option1 === Burner.twoInductionRings
    ) {
      return true;
    }

    return false;
  }

  /**
   * Sully ranges allow wider burners to the left of a 4-feux top only. These
   * "wide" burners are not allowed to the left of a Classique or Traditional
   * range top.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.top]
   */
  hasWideBurnerInNarrowBurnerPosition(desired) {
    const option1 = desired.option1 || this.#state.option1;
    const option2 = desired.option2 || this.#state.option2;
    const top = desired.top || this.#state.top;

    if (
      (this.#state.model === RangeModel.sully ||
        this.#state.model === RangeModel.sully1800right) &&
      top !== RangeTop.fourFeux &&
      this.#isWideBurner(option1)
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.sully1800left ||
        this.#state.model === RangeModel.sully2200) &&
      top !== RangeTop.fourFeux &&
      this.#isWideBurner(option2)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Neither Planchas nor Induction rings can be placed over a gas oven.
   * This only applies to the various Sully ranges.
   *
   * @param {Object} desired - The requested range change from the UI.
   * @param {string} [desired.leftOven]
   * @param {string} [desired.rightOven]
   * @param {string} [desired.option1]
   * @param {string} [desired.option2]
   * @param {string} [desired.option3]
   * @param {string} burner - Either a plancha or induction rings.
   * @returns {boolean}
   */
  #hasElectricOptionOverGasOven(desired, burner) {
    const leftOven = desired.leftOven || this.#state.leftOven;
    const rightOven = desired.rightOven || this.#state.rightOven;
    const option1 = desired.option1 || this.#state.option1;
    const option2 = desired.option2 || this.#state.option2;
    const option3 = desired.option3 || this.#state.option3;

    if (
      (this.#state.model === RangeModel.sully ||
        this.#state.model === RangeModel.sully1800right) &&
      leftOven === Oven.gas &&
      option1 === burner
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.sully ||
        this.#state.model === RangeModel.sully1800right) &&
      rightOven === Oven.gas &&
      option2 === burner
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.sully1800left ||
        this.#state.model === RangeModel.sully2200) &&
      leftOven === Oven.gas &&
      option2 === burner
    ) {
      return true;
    }

    if (
      (this.#state.model === RangeModel.sully1800left ||
        this.#state.model === RangeModel.sully2200) &&
      rightOven === Oven.gas &&
      option3 === burner
    ) {
      return true;
    }

    return false;
  }

  /**
   * Is the oven electric or electric convection?
   * @param {string} oven
   * @returns {boolean}
   */
  #isElectric(oven) {
    return oven === Oven.electric || oven === Oven.convection;
  }

  /**
   * Is the the optional burner wider than a 2 burner 11k or 2 burner 15k unit?
   * @param {string} option
   * @returns {boolean}
   */
  #isWideBurner(option) {
    return (
      option === Burner.one18kBurner ||
      option === Burner.flameGrill ||
      option === Burner.electricPlancha ||
      option === Burner.traditionalSimmerPlate ||
      option === Burner.multiCooker ||
      option === Burner.twoInductionRings
    );
  }
}
